import { makeObservable, observable, action, runInAction, toJS } from "mobx";
import { SearchRequest, FilterItem } from "../api/Types";
import {
  PerPageSettings,
  WithBooleanFlag,
  Sorter,
  Searcher,
  Filter,
  ExpandableKeys,
  StatusInfo,
} from "./core";
import api from "../modules/Nsi/api";
import Dictionary from "../api/Dictionary";
import Constants from "../constants/Common";
import { webAPI } from "../api/Api";
import { downloadRawFileFromServer } from "../api/helpers";
import Common from "../constants/Common";
import * as moment from "moment";
import { message } from "antd";

class DictionaryStore {
  data = {};
  dataInner = {};
  isLoadingDict = new WithBooleanFlag();
  isLoadingVersion = new WithBooleanFlag(false);

  dictData = [];
  dictVersions = [];
  dictHistory = [];
  userDictionary = [];

  dictParamsRowKeys: ExpandableKeys = new ExpandableKeys();
  dictParamsEditableRowKeys: ExpandableKeys = new ExpandableKeys();
  isShowAddForm = new WithBooleanFlag(false);
  isShowAddGroupForm = new WithBooleanFlag(false);
  isShowEditForm = new WithBooleanFlag(false);
  isShowEditGroupForm = new WithBooleanFlag(false);

  isLoading = new WithBooleanFlag();
  isUpdating = new WithBooleanFlag(false);
  dictFormIsSending = new WithBooleanFlag();
  paginator = new PerPageSettings();
  sorter = new Sorter();
  filter: Filter = new Filter();

  search = new Searcher();

  dictStateStatus: StatusInfo = new StatusInfo();

  isImportModalVisible = new WithBooleanFlag(false);
  currentDictItem = { id: null, name: null, title: null };

  searchAbortController = null;

  constructor() {
    makeObservable(
      this,
      {
        data: observable,
        getDictionaryData: action,
        dictData: observable,
        dictVersions: observable,
        dictHistory: observable,
        getDictList: action,
        getUserDictionary: action,
        userDictionary: observable,
        isImportModalVisible: observable,
        currentDictItem: observable,
      },
      {
        deep: true,
      },
    );
  }

  async getDictionaryData(name, forceUpdate = false) {
    if (!forceUpdate && this.data[name] && this.data[name].length) {
      return;
    }
    this.isLoadingDict.setTrue();
    try {
      const res = await webAPI.post(`/nsi/SystemDictionary/${name}/Items`, {
        page: 1,
        count: 0,
      });
      runInAction(() => {
        this.dataInner[name] = res.data.items;
        this.data = Object.assign({}, this.dataInner);
      });
    } catch (error) {
      runInAction(() => {
        console.log(error);
      });
    } finally {
      this.isLoadingDict.setFalse();
    }
  }

  getDictList = async (forceUpdate = true) => {
    if (!forceUpdate && this.dictData.length) {
      return;
    }
    if (this.searchAbortController) {
      this.searchAbortController.abort();
    }
    this.searchAbortController = new AbortController();

    try {
      this.isLoading.setTrue();
      const request: SearchRequest = {
        page: this.paginator?.page,
        count: this.paginator?.perPage,
        sortItems: this.sorter?.sortData,
        filterItems: this.filter?.filterItems,
        search: this.search.getSearch(),
      };
      const res = await api.getCustomDictionaries(request, {
        signal: this.searchAbortController.signal,
      });
      if (!this.searchAbortController.signal.aborted) {
        runInAction(() => {
          const array = [];

          const cb = (e) => {
            const el = e;
            el.keys = new ExpandableKeys();
            el.editableKeys = new ExpandableKeys();
            if (!el.parentGroupId) array.push(el);
            e.groups && e.groups.forEach(cb);
          };
          res.data.items.forEach(cb);
          this.dictData = array;
          this.paginator.updateMeta(res.data);
          // console.log(array, '---array')
        });
        this.isLoading.setFalse();
      }
    } catch (error) {
      console.log(error);
      if(error.message !== 'canceled') {
        this.isLoading.setFalse();
      }
    }
    return toJS(this.dictData);
  };

  saveGroup = async (values) => {
    this.isUpdating.setTrue();
    this.dictStateStatus.resetStatus();

    try {
      const res = await api.saveGroup(values);
      runInAction(() => {
        !values.id
          ? this.dictStateStatus.setStatus(Constants.requestStatuses.created)
          : this.dictStateStatus.setStatus(Constants.requestStatuses.updated);

        this.getDictList(true);
      });
      if (values.id) {
        this.dictParamsRowKeys.remove(values.id);
      } else {
        this.isShowAddGroupForm.toggle();
      }
    } catch (e) {
      this.dictStateStatus.setDetails(e.response.data?.errors);
      this.dictStateStatus.setStatus(Constants.requestStatuses.error);
    } finally {
      this.isUpdating.setFalse();
    }
  };

  saveDict = async (values) => {
    this.isUpdating.setTrue();
    this.dictStateStatus.resetStatus();

    try {
      const res = await api.saveDict(values);
      runInAction(() => {
        !values.id
          ? this.dictStateStatus.setStatus(Constants.requestStatuses.created)
          : this.dictStateStatus.setStatus(Constants.requestStatuses.updated);
        this.getDictList(true);
      });
      if (values.id) {
        this.dictParamsRowKeys.remove(values.id);
      } else {
        this.isShowAddForm.toggle();
      }
    } catch (e) {
      this.dictStateStatus.setStatus(Constants.requestStatuses.error);
    } finally {
      this.isUpdating.setFalse();
    }
  };

  saveDictItem = async (values) => {
    this.dictStateStatus.resetStatus();

    try {
      const res = await api.saveDictItem(values);
      runInAction(() => {
        !values.id
          ? this.dictStateStatus.setStatus(Constants.requestStatuses.created)
          : this.dictStateStatus.setStatus(Constants.requestStatuses.updated);
        this.getDictList(true);
      });
    } catch (e) {
      this.dictStateStatus.setStatus(Constants.requestStatuses.error);
    }
  };

  deleteGroup = async (id) => {
    this.dictStateStatus.resetStatus();

    try {
      const res = await api.deleteGroup(id);
      runInAction(() => {
        this.dictStateStatus.setStatus(Constants.requestStatuses.deleted);
        this.getDictList();
      });
    } catch (e) {
      this.dictStateStatus.setDetails([
        Constants.errorMessages.noDeletingGroupDict,
      ]);
      this.dictStateStatus.setStatus(Constants.requestStatuses.error);
    }
  };

  deleteDict = async (id) => {
    this.dictStateStatus.resetStatus();

    try {
      const res = await api.deleteDict(id);
      runInAction(() => {
        this.dictStateStatus.setStatus(Constants.requestStatuses.deleted);
        this.getDictList();
      });
    } catch (e) {
      this.dictStateStatus.setDetails(e.response.data?.errors);
      this.dictStateStatus.setStatus(Constants.requestStatuses.error);
    }
  };

  deleteDictItem = async (id) => {
    this.dictStateStatus.resetStatus();

    try {
      const res = await api.deleteDictItem(id.id);
      runInAction(() => {
        this.dictStateStatus.setStatus(Constants.requestStatuses.deleted);
        this.getDictList();
      });
    } catch (e) {
      this.dictStateStatus.setStatus(Constants.requestStatuses.error);
    }
  };

  async fillDictItems(id, data) {
    try {
      const res = await api.fillDictItems(id, data);

      runInAction(() => {
        this.dictStateStatus.setStatus(Constants.requestStatuses.created);
        this.getDictList(true);
      });
    } catch (e) {
      this.dictStateStatus.setStatus(Constants.requestStatuses.error);
    }
  }

  getAllItemsVersions = async (dictionaryId) => {
    try {
      const res = await api.getAllItemsVersions(dictionaryId);

      runInAction(() => {
        this.dictVersions = res.data;
      });
    } catch (e) {}
  };

  downloadDictionaryVersion = async ({
    dictionaryId,
    versionDate,
    dictName,
  }) => {
    this.isLoadingVersion.setTrue();
    try {
      const res = await api.getDictionaryVersion(dictionaryId, versionDate);
      console.log(this.isLoadingVersion.value);
      downloadRawFileFromServer(
        res.data,
        `${dictName}_${moment(versionDate).format("DD.MM.YYYY")}.${
          Common.fileFormats[res.headers["content-type"]]
        }`,
      );
    } catch (e) {
      console.log(e);
    } finally {
      this.isLoadingVersion.setFalse();
    }
  };

  lockDictionary = async (dictionaryId) => {
    try {
      const res = await api.lockDictionary(dictionaryId);

      runInAction(() => {
        this.dictVersions = res.data;
      });
    } catch (e) {}
  };

  unlockDictionary = async (dictionaryId) => {
    try {
      const res = await api.unlockDictionary(dictionaryId);

      runInAction(() => {
        this.dictVersions = res.data;
      });
    } catch (e) {}
  };

  getDictinaryHistory = async (dictionaryId) => {
    try {
      const res = await api.getDictinaryHistory(dictionaryId);
      const history = [];
      //const keys = ["Название справочника","Описание"];
      res.data.forEach((item) => {
        Object.keys(item.model) //.filter(key => keys.includes(key))
          .forEach((key) => {
            history.push({
              key,
              modifiedDateTime: item.modifiedDateTime,
              modifiedBy: item.modifiedBy,
              oldValue: item.model[key].value,
              newValue: item.model[key].overridenValue,
            });
          });
      });
      runInAction(() => {
        this.dictHistory = history;
      });
    } catch (e) {}
  };

  // TODO: в дальнейшем убрать этот метод
  getUserDictionary = async (id, keyTitle = "dictionaryValue") => {
    if (!id) {
      return;
    }
    this.isLoading.setTrue();
    try {
      const { data } = await Dictionary.getUserDictionary(id);
      runInAction(() => {
        this.userDictionary = data.map((item) => ({
          id: item.id,
          title: item[keyTitle],
        }));
      });
    } catch (error) {
      runInAction(() => {
        console.log(error);
      });
    } finally {
      this.isLoading.setFalse();
    }
  };

  importDictionary = (formData: FormData) => {
    this.isUpdating.setTrue();
    return api
      .importDictionary(this.currentDictItem.name, formData)
      .then(() => {
        return message.info(Constants.statusMessage.pending);
      })
      .catch((error) => {
        message.error(
          error?.response?.data
            ? error.response.data
            : Constants.statusMessage.error,
        );
        return Promise.reject(error);
      })
      .finally(() => {
        this.isUpdating.setFalse();
      });
  };
}

const dictionaryStore = new DictionaryStore();
export default dictionaryStore;
